import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import { fetchAllBalances } from '../store/walletSlice';
import { SUBGENRES, SUBGENRE_LABELS } from '../data/catalog';
import { getAvatarUrl } from '../utils/media';
import { useAvatarAuth } from '../hooks/useAvatarAuth';
import api from '../api';
import logo from '../assets/logoblack.svg';

const NAV_ITEMS = [
  { label: 'Movies', type: 'movie' },
  { label: 'Series', type: 'series' },
  { label: 'Documentaries', type: 'documentary' },
  { label: 'Courses', type: 'course' },
  { label: 'Mentorships', type: 'mentorship' },
  { label: 'Other', type: 'other' },
];

export default function Navbar() {
  const { user } = useSelector((state) => state.auth);
  const { allBalances } = useSelector((state) => state.wallet);
  const avatarAuth = useAvatarAuth();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [mobileWalletOpen, setMobileWalletOpen] = useState(false);
  const [footerInfoOpen, setFooterInfoOpen] = useState(false);
  const [infoSection, setInfoSection] = useState(null);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const debounceRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user) {
      dispatch(fetchAllBalances());
    }
  }, [user, dispatch]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileProfileOpen(false);
    setBalanceExpanded(false);
    setMobileWalletOpen(false);
    setFooterInfoOpen(false);
  }, [location.pathname]);

  const pathType = location.pathname.replace('/', '');
  const currentType = ['movie', 'series', 'documentary', 'course', 'mentorship', 'other'].includes(pathType) ? pathType : searchParams.get('type') || '';
  const currentGenre = searchParams.get('genre') || 'all';

  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/content/search', { params: { q: q.trim() } });
        setSuggestions(data);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowSuggestions(true);
    fetchSuggestions(val);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/home');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (query.trim()) {
      navigate(`/explore?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleSuggestionClick = (item) => {
    setQuery('');
    setShowSuggestions(false);
    navigate(`/content/${item.id}`);
  };

  const subgenres = currentType && SUBGENRES[currentType]
    ? SUBGENRES[currentType].filter(g => g !== 'all')
    : null;

  return (
    <header className="navbar-wrapper">
      <nav className="navbar">
        <Link to="/explore" className="navbar-logo">
          <img src={logo} alt="Finema" />
        </Link>

        {/* Mobile top bar — unified pill */}
        <div className="mobile-bottom-bar">
          <button
            className={`mobile-bottom-btn mobile-bottom-menu ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileProfileOpen(false); setMobileWalletOpen(false); }}
            aria-label="Toggle menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="hamburger-icon">
              <polyline points="6 15 12 9 18 15" />
            </svg>
          </button>
          <a className="mobile-bottom-btn mobile-bottom-balance" onClick={(e) => {
            e.preventDefault();
            setMobileMenuOpen(false);
            setMobileProfileOpen(false);
            if (location.pathname === '/wallet') {
              navigate(-1);
            } else {
              setMobileWalletOpen(!mobileWalletOpen);
            }
          }}>
            $
          </a>
          <button
            className={`mobile-bottom-btn mobile-bottom-profile ${mobileProfileOpen ? 'open' : ''}`}
            onClick={() => { setMobileProfileOpen(!mobileProfileOpen); setMobileMenuOpen(false); setMobileWalletOpen(false); }}
            aria-label="Toggle profile"
          >
            {user ? (
              user.avatarUrl ? (
                <img src={getAvatarUrl(user.avatarUrl, avatarAuth) || user.avatarUrl} alt="" className="mobile-profile-avatar" />
              ) : (
                <span className="mobile-profile-initial">{(user.displayName || user.email || '?')[0].toUpperCase()}</span>
              )
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mobile-profile-icon">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </button>
        </div>

        <div className="navbar-left-group">
          <div className="navbar-search-wrapper" ref={searchRef}>
            <form className="navbar-search" onSubmit={handleSearch}>
              <input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={handleQueryChange}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />
              <button type="submit" className="navbar-search-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </form>
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions">
                {suggestions.map(item => (
                  <div key={item.id} className="search-suggestion" onMouseDown={() => handleSuggestionClick(item)}>
                    <span className="suggestion-title">{item.title}</span>
                    <span className="suggestion-creator">{item.creator_name || item.creator_username}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {user && (
          <a className="navbar-balance-pill" onClick={(e) => { e.preventDefault(); navigate('/wallet', { state: { reset: Date.now() } }); }}>
            Balance: {allBalances?.totalBalance != null ? parseFloat(parseFloat(allBalances.totalBalance).toFixed(2)) || 0 : 0} $
          </a>
        )}

        <div className="navbar-right">
          {user ? (
            <>
              <div
                className="navbar-user-menu"
                ref={userMenuRef}
                onMouseLeave={() => setShowUserMenu(false)}
              >
                <button
                  className="navbar-avatar-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  {user.avatarUrl ? (
                    <img src={getAvatarUrl(user.avatarUrl, avatarAuth) || user.avatarUrl} alt="" className="navbar-avatar" />
                  ) : (
                    <div className="navbar-avatar navbar-avatar-placeholder">
                      {(user.displayName || user.email || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <span className="navbar-username">{user.username || user.displayName || user.email}</span>
                </button>
                {showUserMenu && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      {user.displayName || user.email}
                    </div>
                    <Link to={user.username ? `/profile/${user.username}` : '/creator/edit-profile'} className="user-dropdown-item" onMouseDown={(e) => e.preventDefault()}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      Profile
                    </Link>
                    <Link to="/creator" className="user-dropdown-item" onMouseDown={(e) => e.preventDefault()}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                      Create
                    </Link>
                    <a className="user-dropdown-item" onMouseDown={(e) => { e.preventDefault(); navigate('/wallet', { state: { reset: Date.now() } }); setShowUserMenu(false); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/></svg>
                      Wallet
                    </a>
                    <Link to="/library" className="user-dropdown-item" onMouseDown={(e) => e.preventDefault()}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                      My Library
                    </Link>
                    <Link to="/settings" className="user-dropdown-item" onMouseDown={(e) => e.preventDefault()}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      Settings
                    </Link>
                    <div className="user-dropdown-divider" />
                    <button className="user-dropdown-item user-dropdown-logout" onMouseDown={(e) => { e.preventDefault(); handleLogout(); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-outline">Log In</Link>
              <Link to="/register" className="btn btn-primary">Sign Up</Link>
            </>
          )}
        </div>
      </nav>
      <div className="navbar-top-bar" />

      {/* Desktop category menu */}
      <div className="navbar-menu navbar-menu-desktop">
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.label}
            to={`/${item.type}`}
            className={`navbar-menu-item ${currentType === item.type ? 'active' : ''}`}
          >
            {item.label}
          </Link>
        ))}
      </div>

      {/* Mobile nav drawer (hamburger) */}
        <div className={`mobile-drawer mobile-drawer-slide-left ${mobileMenuOpen ? 'mobile-drawer-visible' : ''}`}>
          <div className="mobile-drawer-search">
            <form className="navbar-search" onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }}>
              <input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={handleQueryChange}
              />
              <button type="submit" className="navbar-search-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </form>
          </div>
          <span className="mobile-drawer-nav-label">Choose your category</span>
          <div className="mobile-drawer-nav">
            <div className="mobile-drawer-scroll">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.label}
                  to={`/${item.type}`}
                  className={`mobile-drawer-item ${currentType === item.type ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          {SUBGENRES[currentType || 'movie'] && (
            <>
              <span className="mobile-drawer-nav-label">Choose subgenre</span>
              <div className="mobile-drawer-nav">
                <div className="mobile-drawer-scroll">
                  {SUBGENRES[currentType || 'movie'].map((g) => (
                    <Link
                      key={g}
                      to={g === 'all' ? `/${currentType || 'movie'}` : `/${currentType || 'movie'}?genre=${g}`}
                      className={`mobile-drawer-item ${currentGenre === g ? 'active' : ''}`}
                    >
                      {SUBGENRE_LABELS[g] || g}
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

      {/* Mobile wallet drawer (balance) */}
        <div className={`mobile-drawer mobile-drawer-wallet mobile-drawer-slide-right ${mobileWalletOpen ? 'mobile-drawer-visible' : ''}`}>
          <div className="mobile-wallet-summary">
            <span className="mobile-wallet-label">Your Balance</span>
            <span className="mobile-wallet-amount">{user && allBalances?.totalBalance != null ? parseFloat(parseFloat(allBalances.totalBalance).toFixed(2)) || 0 : 0} $</span>
          </div>
          <div className="mobile-drawer-actions">
            <Link to="/wallet" className="mobile-drawer-link">Open Wallet</Link>
            <Link to="/deposit-guide" className="mobile-drawer-link">Deposit</Link>
          </div>
        </div>

      {/* Mobile profile drawer (avatar) */}
        <div className={`mobile-drawer mobile-drawer-profile mobile-drawer-slide-right ${mobileProfileOpen ? 'mobile-drawer-visible' : ''}`}>
          {!user ? (
            <div className="mobile-drawer-auth">
              <Link to="/login" className="btn btn-outline">Log In</Link>
              <Link to="/register" className="btn btn-primary">Sign Up</Link>
            </div>
          ) : (
            <>
              <div className="mobile-drawer-actions">
                <Link to={user.username ? `/profile/${user.username}` : '/creator/edit-profile'} className="mobile-drawer-link">Profile</Link>
                <Link to="/creator" className="mobile-drawer-link">Create</Link>
                <Link to="/wallet" className="mobile-drawer-link">Wallet</Link>
                <Link to="/library" className="mobile-drawer-link">My Library</Link>
                <Link to="/settings" className="mobile-drawer-link">Settings</Link>
                <button className="mobile-drawer-link mobile-drawer-logout" onClick={handleLogout}>Log Out</button>
              </div>
            </>
          )}
        </div>
    </header>
  );
}
